// PageProjectViewPlanner Component

import _ from 'lodash'
import React from 'react'

import app from '../../../app.jsx'
import DateUtil from '../../../lib/dateutil.jsx'
import eventfilters from '../../../lib/eventfilters.jsx'
import BaseProjectView from '../../page/base_projectview/base_projectview.jsx'
import ProfilerUtil from '../../../lib/profilerutil.jsx'
import PlannerPageProvider from '../../../lib/planner_pageprovider.jsx'
import IntlUtil from '../../../lib/intlutil.jsx'

class PageProjectViewPlanner extends BaseProjectView {
  constructor (props) {
    super(props, 'projectview-planner', 'project', 'projectview-planner')

    const fromDate = DateUtil.getMondayByDate(new Date())

    this.data = {
      projectId: this.props.projectId,
      project: this._getProjectFromCache(this.props.projectId),
      stat: this._getOverviewStatFromCache(this.props.projectId),
      pageState: {
        sort: 'title',
        sortAsc: true,
        filters: [],
        filter: {
          contactIds: [],
          projectIds: [],
          userIds: [],
          fromDate: null,
          toDate: null
        },
        viewMode: 'day' /* day | week */,
        visibleObjects: this._getAllVisibleObjects(),
        visibleHours: ['planned'],
        expandObjectIds: null
      },
      context: {
        contacts: [],
        projects: [],
        activities: [],
        tasks: [],
        schedules: [],
        timecards: [],
        projectOptions: [],
        skillOptions: []
      },
      objects: [],
      histories: [],
      kanbanInfo: this._getDefaultKanbanInfo(),
      currentEditObject: null,
      fromDate: fromDate,
      toDate: DateUtil.addDaysToDate(fromDate, 14),
      taskFromDate: null,
      taskToDate: null,
      dayItems: [],
      showToolbarActions: true,
      initialContactId: null,
      initialProjectId: null
    }

    this.state = {
      profilerId: null
    }

    this._references = {
      sidebarTask: React.createRef(),
      dialogTimeCardDetail: React.createRef(),
      droppablePagebreakTop: React.createRef(),
      uploadErrorDialog: React.createRef()
    }
    this.createCommonReference(this._references)

    /* Initialize profiler */
    this._profiler = new ProfilerUtil(this._pageIdent, 3) // Average time 3 seconds
    this._profiler.start()
    this._profiler.setParams(this.props.projectId)

    this._bUnMounted = false
    this._phaseState = {}
    this._timerTaskHourUpdate = null
  }

  componentDidMount () {
    this._initEvents()
    app.updatePageTitle('project-view', 'Planner')
    this._getData(false)

    // Calculate and save profiler, Hardcode profiler id as 1, since it is the first call of profiler
    this._profiler.stop(1)
  }

  componentWillUnmount () {
    this._bUnMounted = true
    this._profiler = null

    this._releaseEvents()
  }

  componentDidUpdate (prevProps, prevState) {
    if (
      this._profiler &&
      (!prevState || prevState.profilerId !== this.state.profilerId) &&
      this.state.profilerId
    ) {
      this._profiler.stop(this.state.profilerId)
    }
  }

  getContentClassname () {
    return 'full'
  }

  _initializeUserFilters () {
    const { pageState, project } = this.data

    if (project && project.contact) {
      const filters = pageState.filters || []
      const contactId = project.contact.id
      const projectId = project.id

      // Add current project and contact as default filter
      filters.unshift(
        this.getProjectFilterObject(projectId, project.title),
        this.getContactFilterObject(contactId, project.contact.company)
      )

      pageState.filters = filters
      pageState.filter.contactIds = [contactId]
      pageState.filter.projectIds = [projectId]
    }
  }

  getProjectFilterObject (projectId, title) {
    return {
      ident: 'project',
      typeLabel: IntlUtil.convertMessage('common.newfilter.lableIdentProject'),
      value: projectId,
      text: title,
      hideDelete: true
    }
  }

  getContactFilterObject (contactId, title) {
    return {
      ident: 'contact',
      typeLabel: IntlUtil.convertMessage('common.newfilter.lableIdentContact'),
      value: contactId,
      text: title,
      hideDelete: true
    }
  }

  /* Render functions */
  render () {
    return this.renderDetailPageContent()
  }

  renderContent () {
    return this._renderPlannerContent()
  }

  renderRightSidebar () {
    return this._renderRightSidebarPlanner()
  }

  _getAllVisibleObjects () {
    return ['phase', 'activity', 'task']
  }

  /* Data functions */
  _getSaveObjectPageState (pageState) {
    const { project } = this.data
    const savePageStateData = _.cloneDeep(pageState)

    _.remove(savePageStateData.filters, filter => filter.hideDelete)

    if (savePageStateData.filter) {
      if (savePageStateData.filter.contactIds && project.contact) {
        _.remove(
          savePageStateData.filter.contactIds,
          contactId => contactId === project.contact.id
        )
      }

      if (savePageStateData.filter.projectIds && project) {
        _.remove(
          savePageStateData.filter.projectIds,
          projectId => projectId === project.id
        )
      }
    }

    return savePageStateData
  }

  _getData (_bReload) {
    const { taskFromDate, taskToDate, projectId } = this.data
    const { fromDate, toDate } = this._calculateFromAndToDates()

    this.data.fromDate = fromDate
    this.data.toDate = toDate

    this._calculateDayItems()

    const params = {
      query: {
        teamId: app.getTeamId(),
        pageIdent: this._pageIdent,
        project: {
          $client: {
            subObjects: {
              contact: ['company']
            }
          }
        },
        planner: {
          pageIdent: this._pageIdent,
          taskFromDate: taskFromDate,
          taskToDate: taskToDate,
          clientTimezoneOffset: DateUtil.getLocalTimezoneOffset(),
          $client: {
            initialProject: {
              projectId,
              labelProject: IntlUtil.convertMessage(
                'common.newfilter.lableIdentProject'
              ),
              labelContact: IntlUtil.convertMessage(
                'common.newfilter.lableIdentContact'
              )
            },
            getUser: true
          }
        }
      }
    }

    return app
      .service('/api/page/project/planner')
      .get(projectId, params)
      .then(plannerRes => {
        this._onPageDataRecieved(plannerRes.pageData)
        this.data.project = plannerRes.project
        this.data.pageState = plannerRes.pageState
        this.data.context = plannerRes || null
        this.data.histories = plannerRes.histories || []

        const pageUserState = this._getPageUserState(
          plannerRes.pageData.userState
        )
        if (pageUserState && !pageUserState.showToolbarActions) {
          this.data.showToolbarActions = false
        }

        this._calculateResult(plannerRes)

        if (
          this.data.pageState.expandObjectIds === null ||
          this.data.pageState.expandObjectIds === undefined
        ) {
          this.data.pageState.expandObjectIds = []

          this.data.objects.forEach(contact => {
            if (contact.projects) {
              contact.projects.forEach(prjItem => {
                if (prjItem.id === this.data.project.id) {
                  this._autoExpandObjects(prjItem, contact.id)
                }
              })
            }
          })
        }

        this._onDataReceived(null)
        this._setProjectToCache(this.data.project)
        app.updatePageTitle(
          'projectview-planner',
          this.data.project ? this.data.project.title : ''
        )
      })
      .catch(err => {
        this._onDataReceived(err)
      })
  }

  _autoExpandObjects (project, contactId) {
    const expandObjectIds = this.data.pageState.expandObjectIds

    expandObjectIds.push(contactId)
    expandObjectIds.push(project.id)

    project.phases.forEach(phase => {
      expandObjectIds.push(phase.id)

      /* phase.activities.forEach((activity) => {
        expandObjectIds.push(activity.id);

        activity.tasks.forEach((task) => {
          expandObjectIds.push(task.id);
        });
      }); */
    })

    this.data.pageState.expandObjectIds = expandObjectIds
  }

  _reloadData () {
    this._getData(true)
  }

  _onDataReceived (err) {
    if (this._bUnMounted) return

    if (err) {
      app.debug('PagePlanner: _onDataReceived Error', err)
    } else {
      app.debug('PagePlanner: _onDataReceived [done]')
    }

    this.setState({ dataDate: new Date() })
  }

  /* Realtime Events */
  _initEvents () {
    this._initTaskEvents()
    this._initActivityEvents()
    this._initProjectEvents()
  }

  _releaseEvents () {
    this._releaseTaskEvents()
    this._releaseActivityEvents()
    this._releaseProjectEvents()
  }

  _eventReloadTask (objectName, eventName, eventData) {
    if (eventfilters.teamFilter(eventData)) {
      app.debug(`-- Event - PagePlanner - ${objectName} - ${eventName} --`)
      this._reloadData()
    }
  }

  _eventReloadActivity (objectName, eventName, eventData) {
    if (eventfilters.teamFilter(eventData)) {
      app.debug(`-- Event - PagePlanner - ${objectName} - ${eventName} --`)
      this._reloadData()
    }
  }

  _eventReloadProject (objectName, eventName, eventData) {
    if (eventfilters.teamFilter(eventData)) {
      app.debug(`-- Event - PagePlanner - ${objectName} - ${eventName} --`)
      this._reloadData()
    }
  }
}

export default PlannerPageProvider(PageProjectViewPlanner)
